import { GOOGLE_CLIENT_ID, SERVER_URL, USER_WORKER_URL } from '../config.js';
import { tokenManager } from './TokenManager.js';
import { storage } from './storage.js';
import { resolveLoginAgePrefs, setAgeContentPrefs } from './userPrefs.js';
import { normalizeLanguageCode } from './languageData.js';

const GSI_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

/** @type {Promise<void> | null} */
let gsiLoadPromise = null;
let promptInFlight = false;
let authPending = false;
let credentialStarted = false;

/**
 * True while One Tap is open, or until the id-token request settles after an account click.
 * @returns {boolean}
 */
export const isGoogleOneTapAuthPending = () => authPending;

const releaseNavHold = () => {
  if (credentialStarted) return;
  authPending = false;
};

const releaseNavHoldAfterCurrentClick = () => {
  setTimeout(releaseNavHold, 0);
};

/**
 * Skip and non-credential dismiss only. Display and skip-reason APIs are unreliable under FedCM.
 * @param {any} notification
 */
const handlePromptMoment = (notification) => {
  try {
    if (notification?.isDismissedMoment?.()) {
      if (notification.getDismissedReason?.() === 'credential_returned') return;
      releaseNavHoldAfterCurrentClick();
      return;
    }
    if (notification?.isSkippedMoment?.()) {
      releaseNavHoldAfterCurrentClick();
    }
  } catch { /* ignore */ }
};

/**
 * @returns {boolean}
 */
const isGuestUser = () => {
  try {
    if (tokenManager.isAuthenticated?.()) return false;
  } catch { /* ignore */ }

  try {
    const userData = storage.get('userData');
    if (userData?.id && userData?.isLoggedIn) return false;
  } catch { /* ignore */ }

  return true;
};

/**
 * @param {string | null | undefined} languageCode
 * @returns {string}
 */
const normalizeLanguageForAuth = (languageCode) => normalizeLanguageCode(languageCode);

/**
 * @returns {string | null}
 */
const getPreferredLanguageCodeForAuth = () => {
  try {
    const selectedLanguage = normalizeLanguageForAuth(storage.getLanguage());
    return selectedLanguage.toLowerCase() !== 'en' ? selectedLanguage : null;
  } catch {
    return null;
  }
};

/**
 * @returns {Promise<{ nonce: string, hashedNonce: string }>}
 */
const generateNoncePair = async () => {
  const nonce = btoa(String.fromCharCode(...crypto.getRandomValues(new Uint8Array(32))));
  const hashBuffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(nonce));
  const hashedNonce = Array.from(new Uint8Array(hashBuffer))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
  return { nonce, hashedNonce };
};

/**
 * @returns {Promise<void>}
 */
const loadGsiClient = () => {
  if (typeof window !== 'undefined' && window.google?.accounts?.id) {
    return Promise.resolve();
  }

  if (gsiLoadPromise) return gsiLoadPromise;

  gsiLoadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${GSI_SCRIPT_SRC}"]`);
    if (existing) {
      if (window.google?.accounts?.id) {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => {
        gsiLoadPromise = null;
        reject(new Error('Failed to load Google Identity Services'));
      }, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = GSI_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      gsiLoadPromise = null;
      reject(new Error('Failed to load Google Identity Services'));
    };
    document.head.appendChild(script);
  });

  return gsiLoadPromise;
};

/**
 * @param {any} result
 * @returns {Promise<void>}
 */
const applyAuthSuccess = async (result) => {
  const userDataObj = {
    id: result.user.id,
    name: result.user.name || 'Anonymous',
    email: result.user.email || '',
    avatar: result.user.avatar || result.user.avatar_url || '/assets/images/characters/unknown.png',
    gender_preference: result.user.gender_preference,
    isLoggedIn: true,
    language: result.user.language || null,
    current_tier: result.user.current_tier || 'free',
    sprite_credits: result.user.sprite_credits || 0,
    boughtTrial: result.user.boughtTrial || false,
    over_18: result.user.over_18 === true,
    nsfw: result.user.over_18 === true && result.user.nsfw === true
  };

  const loginAgePrefs = resolveLoginAgePrefs(result.user);
  userDataObj.over_18 = loginAgePrefs.over18;
  userDataObj.nsfw = loginAgePrefs.nsfw;

  try { storage.remove('userData'); } catch { /* ignore */ }

  tokenManager.setTokens(result.token, result.refreshToken || null, userDataObj);
  setAgeContentPrefs({ over18: loginAgePrefs.over18, nsfw: loginAgePrefs.nsfw });

  const needsPreferenceUpdate = result.user.language === null || result.user.gender_preference === null || loginAgePrefs.shouldSyncGuest;
  if (needsPreferenceUpdate) {
    try {
      const storedLanguage = storage.getLanguage();
      const storedGenderPreference = storage.get('gender_preference') === 'true' ? 'male_only' : 'female_only';
      const profileUpdates = {};

      if (result.user.language === null && storedLanguage) {
        profileUpdates.language = storedLanguage;
      }
      if (
        result.user.gender_preference === null
        && storage.get('gender_preference') !== null
        && storage.get('gender_preference') !== undefined
      ) {
        profileUpdates.interest = storedGenderPreference;
      }
      if (loginAgePrefs.shouldSyncGuest) {
        profileUpdates.over_18 = loginAgePrefs.over18;
        profileUpdates.nsfw = loginAgePrefs.nsfw;
      }

      if (Object.keys(profileUpdates).length > 0) {
        const profileResponse = await fetch(`${USER_WORKER_URL}/update-profile`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${result.token}`
          },
          body: JSON.stringify(profileUpdates)
        });

        if (profileResponse.ok) {
          const profileData = await profileResponse.json();
          const updatedValues = profileData?.updatedValues;
          if (updatedValues?.gender_preference !== undefined) {
            storage.updateJson(
              'userData',
              'gender_preference',
              updatedValues.gender_preference === 'male_only'
            );
            userDataObj.gender_preference = updatedValues.gender_preference === 'male_only';
          }
          if (updatedValues?.language !== undefined) {
            storage.updateJson('userData', 'language', updatedValues.language);
            userDataObj.language = updatedValues.language;
          }
          if (updatedValues?.over_18 !== undefined || updatedValues?.nsfw !== undefined) {
            const over18 = updatedValues.over_18 === true;
            const nsfw = over18 && updatedValues.nsfw === true;
            setAgeContentPrefs({ over18, nsfw });
            userDataObj.over_18 = over18;
            userDataObj.nsfw = nsfw;
          }
        }
      }
    } catch (error) {
      console.warn('[GoogleOneTap] Failed to sync local preferences:', error);
    }
  }

  // Match callback.html: dispatch auth events so Navigation re-renders with the avatar.
  document.dispatchEvent(new CustomEvent('authSuccess', {
    detail: { user: userDataObj }
  }));
  document.dispatchEvent(new CustomEvent('auth:login:success', {
    detail: { user: userDataObj, authTrigger: 'google_one_tap' }
  }));
  document.dispatchEvent(new CustomEvent('usernameUpdated', {
    detail: { username: userDataObj.name }
  }));
};

/**
 * @param {{ credential?: string }} response
 * @param {string} rawNonce
 * @returns {Promise<void>}
 */
const handleCredentialResponse = async (response, rawNonce) => {
  const idToken = response?.credential;
  if (!idToken) throw new Error('Missing Google credential');

  const requestBody = {
    id_token: idToken,
    nonce: rawNonce
  };
  const preferredLanguageCode = getPreferredLanguageCodeForAuth();
  if (preferredLanguageCode) {
    requestBody.preferred_language_code = preferredLanguageCode;
  }

  const res = await fetch(`${SERVER_URL}/api/v1/auth/google/id-token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(requestBody)
  });

  let result;
  try {
    result = await res.json();
  } catch {
    throw new Error('Invalid auth response from server');
  }

  if (!res.ok || !result?.success) {
    throw new Error(result?.error || 'Google One Tap auth failed');
  }

  await applyAuthSuccess(result);
};

/**
 * Cancel any visible One Tap prompt.
 */
export const cancelGoogleOneTap = () => {
  try {
    window.google?.accounts?.id?.cancel?.();
  } catch { /* ignore */ }
  promptInFlight = false;
  releaseNavHold();
};

/**
 * Show Google One Tap for guests when eligible.
 * Google may suppress the prompt (cooldown, FedCM, signed-out of Google, etc.).
 * @returns {Promise<{ shown: boolean, reason?: string }>}
 */
export const maybeShowGoogleOneTap = async () => {
  if (promptInFlight) return { shown: false, reason: 'in_flight' };
  if (!isGuestUser()) return { shown: false, reason: 'authenticated' };

  promptInFlight = true;

  try {
    await loadGsiClient();

    if (!window.google?.accounts?.id) {
      promptInFlight = false;
      return { shown: false, reason: 'gsi_unavailable' };
    }

    if (!isGuestUser()) {
      promptInFlight = false;
      return { shown: false, reason: 'authenticated' };
    }

    const { nonce, hashedNonce } = await generateNoncePair();

    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: (credentialResponse) => {
        credentialStarted = true;
        authPending = true;
        handleCredentialResponse(credentialResponse, nonce)
          .catch((error) => {
            console.error('[GoogleOneTap] Credential handling failed:', error);
          })
          .finally(() => {
            authPending = false;
            credentialStarted = false;
          });
      },
      nonce: hashedNonce,
      use_fedcm_for_prompt: true,
      context: 'signin',
      auto_select: false
    });

    try {
      credentialStarted = false;
      authPending = true;
      window.google.accounts.id.prompt(handlePromptMoment);
    } catch (error) {
      console.warn('[GoogleOneTap] prompt() failed:', error);
      promptInFlight = false;
      releaseNavHold();
      return { shown: false, reason: 'prompt_error' };
    }

    return { shown: true };
  } catch (error) {
    console.warn('[GoogleOneTap] Failed to show prompt:', error);
    promptInFlight = false;
    releaseNavHold();
    return { shown: false, reason: 'error' };
  }
};
